import React, {createContext, ReactElement, useCallback, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'

import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'
import {authenticationErrored} from '@vio/core/src/auth/business/authenticationErrorEvent'
import {removeOnAuthSuccessLandingUrl} from '@vio/core/src/auth/business/persistData'
import {trackUserAuthClick} from '@vio/core/src/auth/modules/actions'
import {
  getAuthenticationFlow,
  getPasswordlessLoginSourceComponent,
  getUnlockHotelId
} from '@vio/core/src/auth/modules/selectors'
import {Connections} from '@vio/core/src/auth/modules/selectors.shared'
import {
  cleanUpLoginErrors,
  hideBottomSheetLogin,
  resetSusiState,
  SusiHistoryState
} from '@vio/core/src/auth/modules/slice'
import {setSusiState} from '@vio/core/src/auth/modules/slice'
import {makeSocialLogin} from '@vio/core/src/auth/modules/thunks'
import {endPreAuthAttempt} from '@vio/core/src/auth/services'
import {
  AuthenticationFlows,
  AuthenticationMethod,
  SourceComponentType
} from '@vio/core/src/auth/types/Auth'
import {OfferInfoType} from '@vio/core/src/offer/types/offer'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

import {clearFreezeIntent} from '../../../../dealFreeze/utils/freezeIntent'
import {clearDealFreezeStash} from '../../../../dealFreeze/utils/stash'
import {removeSusiQueryParam} from '../../../hooks/useSUSIBottomSheet'
import {AUTHENTICATION_CLICK_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '../../constants'
import {SusiBottomSheet} from './SusiBottomSheet'
import {
  HandleLogin,
  LoginOrigin,
  PasswordlessLoginPropsType,
  SusiContextProps,
  TrackSignInError
} from './types'

export const SusiContext = createContext<SusiContextProps>(
  {} as SusiContextProps
)

export const SusiProvider = ({
  brand,
  children,
  userCountryCode,
  anonymousId
}: PasswordlessLoginPropsType) => {
  // Skip rendering Overlay on the Server + first client render
  const isHydrated = useIsHydrated()

  const [titleSlot, setTitleSlot] = useState<ReactElement | null>(null)
  const [mainContentSlot, setMainContentSlot] = useState<ReactElement | null>(
    null
  )
  const [callbackUrl, setCallbackUrl] = useState<string | null>(null)
  const [bottomContentSlot, setBottomContentSlot] =
    useState<ReactElement | null>(null)
  const [infoContext, setInfoContext] = useState<Record<string, unknown>>({})
  const [skipCloseAnimation, setSkipCloseAnimation] = useState(false)
  const dispatch = useDispatch()
  const unlockHotelId = useSelector(getUnlockHotelId)
  const flow = useSelector(getAuthenticationFlow)
  const [authenticationMethod, setAuthenticationMethod] =
    useState<AuthenticationMethod>()
  const sourceComponent = useSelector(getPasswordlessLoginSourceComponent)
  const {auth} = useBrandCapabilities()

  const getInfoType = useCallback((): OfferInfoType => {
    const gemsPrice = (infoContext?.gemsPrice ?? 0) as number
    const otherSitesPrice = (infoContext?.otherSitesPrice ?? 0) as number
    return gemsPrice < otherSitesPrice
      ? OfferInfoType.Discount
      : OfferInfoType.Parity
  }, [infoContext])

  const updateSUSILocationState = (newState: SusiHistoryState) => {
    dispatch(setSusiState(newState))
  }

  const trackSusiDisplayed = (
    flow: AuthenticationFlows,
    data?: Record<string, unknown>
  ) => {
    const analyticsData = data ?? {}

    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.AuthenticationOverlay,
        action: Action.Displayed,
        team: Team.Retention,
        payload: {
          infoType: getInfoType(),
          isReferrerSusi: false,
          authenticationFlow: flow,
          ...analyticsData
        }
      })
    )
  }

  const trackSignInError: TrackSignInError = errorContext => {
    dispatch(
      authenticationErrored({
        ...errorContext,
        sourceComponent: sourceComponent as SourceComponentType,
        authenticationMethod
      })
    )
  }

  // Resolved the same way as the click event below, so the pair cannot disagree.
  // Persistence drops a context with no source, hence the last fallback.
  const handleSocialLoginClick = (
    socialConnection: Connections,
    analyticsData?: Record<string, unknown>,
    origin?: LoginOrigin
  ) => {
    setTimeout(
      () =>
        dispatch(
          makeSocialLogin({
            connection: socialConnection,
            unlockHotelId: unlockHotelId as string,
            sourceComponent: (origin?.sourceComponent ??
              sourceComponent ??
              SourceComponentType.Login) as SourceComponentType,
            callbackUrl: callbackUrl as string,
            flow: origin?.flow ?? flow,
            analyticsData
          })
        ),
      AUTHENTICATION_CLICK_TIMEOUT_BEFORE_NAVIGATING_AWAY
    )
  }

  const handleLogin: HandleLogin =
    (authenticationMethod, provider, analyticsData, origin) => () => {
      setAuthenticationMethod(authenticationMethod)

      dispatch(
        trackUserAuthClick({
          authenticationMethod,
          sourceComponent: (origin?.sourceComponent ??
            sourceComponent ??
            SourceComponentType.Login) as SourceComponentType,
          analyticsData,
          authenticationFlow: (origin?.flow ?? flow) as AuthenticationFlows
        })
      )

      const socialLoginMapped = {
        [AuthenticationMethod.Google]: Connections.Google,
        [AuthenticationMethod.Facebook]: Connections.Facebook,
        [AuthenticationMethod.Apple]: Connections.Apple,
        [AuthenticationMethod.OIDCFederation]: Connections.OIDCFederation
      }

      handleSocialLoginClick(
        socialLoginMapped[authenticationMethod],
        analyticsData,
        origin
      )
    }

  const handleCloseOverlay = (options?: {
    isEmailHandoff?: boolean
    isFreezeOverlayHandoff?: boolean
  }) => {
    // The email handoff continues in the OTP overlay, so it is not a dismissal.
    if (!options?.isEmailHandoff) {
      endPreAuthAttempt()

      // Drop the pending offer and landing URL so a later sign-in elsewhere
      // does not unexpectedly resume and auto-freeze. The freeze-overlay
      // handoff keeps the stash, which a re-commit needs.
      if (flow === AuthenticationFlows.DealFreeze) {
        if (!options?.isFreezeOverlayHandoff) clearDealFreezeStash()
        clearFreezeIntent()
        removeOnAuthSuccessLandingUrl()
      }

      // The gate stored the checkout URL of the offer the user walked away
      // from; a later sign-in that does not set its own URL must not land there.
      if (flow === AuthenticationFlows.PreCheckout) {
        removeOnAuthSuccessLandingUrl()
      }
    }
    dispatch(cleanUpLoginErrors())
    dispatch(hideBottomSheetLogin())
    setCallbackUrl(null)
    removeSusiQueryParam()
    dispatch(resetSusiState())
  }

  return (
    <SusiContext.Provider
      value={{
        authenticationMethod,
        brand,
        handleCloseOverlay,
        setSkipCloseAnimation,
        handleLogin,
        infoContext,
        setBottomContentSlot,
        setInfoContext,
        setMainContentSlot,
        setTitleSlot,
        trackSignInError,
        trackSusiDisplayed,
        updateSUSILocationState,
        userCountryCode,
        anonymousId,
        setCallbackUrl
      }}
    >
      {children}
      {isHydrated && auth.showSignIn && (
        <SusiBottomSheet
          TitleSlot={titleSlot}
          MainContentSlot={mainContentSlot}
          BottomContentSlot={bottomContentSlot}
          skipCloseAnimation={skipCloseAnimation}
        />
      )}
    </SusiContext.Provider>
  )
}
